import { authedFetch } from "./api";
import type { MovieDetail, MovieDetailsResponse, Rating } from "./types";

// Fetching the ratings / description / cast that fill in a movie card after the
// showtimes have already rendered.
//
// The showtimes request answers "what is playing" and must never wait on this;
// `services/movie_details.py` explains the server half of that split.

/**
 * Titles per request.
 *
 * Small on purpose. A date carries ~40 movies and the server enriches a chunk
 * in parallel, so one big request would mean the whole page's ratings appear at
 * once after the slowest single lookup -- or not at all, if that request fails.
 * Ten fills the visible cards first and makes a failure cost one chunk.
 */
export const DETAIL_CHUNK_SIZE = 10;

/** Split into request-sized chunks, preserving order. */
export function chunkTitles(titles: string[], size = DETAIL_CHUNK_SIZE): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < titles.length; i += size) {
    chunks.push(titles.slice(i, i + size));
  }
  return chunks;
}

function isRating(value: unknown): value is Rating {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return typeof entry.source === "string" && typeof entry.display === "string";
}

/**
 * Coerce one entry from the wire into a `MovieDetail` the card can render.
 *
 * The card asking for this is ALREADY on screen with its showtimes, and the
 * whole point of the split is that details can never take that away. Reading
 * the response as trusted breaks that promise for real: an unexpected shape
 * makes `detail.cast.length` throw during render, React unmounts the tree, and
 * a missing ratings row becomes a blank page. Version skew is the ordinary way
 * that happens -- a cached bundle talking to a newer API, or a Hosting rewrite
 * that answers with the SPA's own index.html.
 *
 * Returns null for an entry with no usable title, since there is no card to
 * attach it to.
 */
export function asMovieDetail(raw: unknown): MovieDetail | null {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw as Record<string, unknown>;
  if (typeof entry.title !== "string" || entry.title === "") return null;

  const text = (value: unknown) => (typeof value === "string" && value ? value : null);
  const verdict =
    typeof entry.verdict === "object" && entry.verdict !== null
      ? (entry.verdict as Record<string, unknown>)
      : null;
  return {
    title: entry.title,
    available: entry.available === true,
    // Per ENTRY, not just per array: `RatingsRow` reads `.group` off each one,
    // and a null in the list would throw there for the same reason a missing
    // `cast` throws in the blurb.
    ratings: Array.isArray(entry.ratings) ? entry.ratings.filter(isRating) : [],
    description: text(entry.description),
    tagline: text(entry.tagline),
    director: text(entry.director),
    cast: Array.isArray(entry.cast) ? entry.cast.filter((c) => typeof c === "string") : [],
    poster_url: text(entry.poster_url),
    trailer_youtube_id: text(entry.trailer_youtube_id),
    verdict: {
      critics: text(verdict?.critics),
      audience: text(verdict?.audience),
    },
    known_for: asKnownFor(entry.known_for),
    photos: asPhotos(entry.photos),
  };
}

/** Name -> headshot, keeping only TMDb image URLs, since each lands in an <img src>. */
function asPhotos(raw: unknown): MovieDetail["photos"] {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>).filter(
      (pair): pair is [string, string] =>
        typeof pair[1] === "string" && pair[1].startsWith("https://image.tmdb.org/"),
    ),
  );
}

/** Name -> titles, keeping only well-formed titles and names that keep any. */
function asKnownFor(raw: unknown): MovieDetail["known_for"] {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};
  const out: MovieDetail["known_for"] = {};
  for (const [name, titles] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(titles)) continue;
    const kept = titles.flatMap((t) => {
      if (typeof t !== "object" || t === null) return [];
      const { title, year } = t as Record<string, unknown>;
      if (typeof title !== "string" || !title) return [];
      return [{ title, year: typeof year === "number" ? year : null }];
    });
    if (kept.length > 0) out[name] = kept;
  }
  return out;
}

export interface DetailRequest {
  date: string;
  cityId: string | null;
  theaterIds: string[];
  titles: string[];
}

/** The (date, titles) pairs a page's movies need, in view order.
 *
 *  `/api/movies/details` takes ONE date, because it finds each title among that
 *  date's showtimes before enriching it. A range view therefore cannot ask
 *  about all of its movies at once: a film playing only next Friday is not in
 *  today's showtimes, and asking about it under today's date comes back
 *  `available: false` -- a card that permanently says "no details found" for a
 *  film TMDb knows perfectly well.
 *
 *  So each movie is asked about under the FIRST date it actually plays, and
 *  movies sharing that date travel together. A single-date response has no
 *  `dates` at all and collapses to one group under `fallbackDate`, which is the
 *  shape this had before ranges existed.
 */
export function detailBatches(
  movies: { title: string; dates?: { date: string }[] }[],
  fallbackDate: string,
): { date: string; titles: string[] }[] {
  const byDate = new Map<string, string[]>();
  for (const movie of movies) {
    const date = movie.dates?.[0]?.date ?? fallbackDate;
    const titles = byDate.get(date);
    if (titles) titles.push(movie.title);
    else byDate.set(date, [movie.title]);
  }
  return [...byDate.entries()].map(([date, titles]) => ({ date, titles }));
}

/**
 * Fetch details a chunk at a time, handing each batch over as it lands.
 *
 * Chunks run SEQUENTIALLY, not in parallel. In view order they fill the cards
 * you are actually looking at first, and running them concurrently would
 * multiply the server's own fan-out by the number of chunks -- against TMDb,
 * OMDb and RT, which are third-party APIs with their own rate limits. This is
 * the "same-host fan-out is serialized" rule; the parallelism that helps lives
 * inside one request.
 *
 * A failed chunk is SKIPPED, not retried and not fatal: the cards it covers
 * keep their showtimes and simply never gain a ratings row. Details are an
 * enhancement, and an error banner for them would be louder than the thing it
 * reports.
 *
 * `cancelled` is polled between chunks so switching dates abandons the rest of
 * the run rather than writing stale details over the new date's cards.
 */
export async function fetchDetails(
  req: DetailRequest,
  onChunk: (details: MovieDetail[]) => void,
  cancelled: () => boolean,
): Promise<void> {
  for (const titles of chunkTitles(req.titles)) {
    if (cancelled()) return;
    try {
      const res = await authedFetch("/api/movies/details", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: req.date,
          titles,
          city: req.cityId,
          theaters: req.theaterIds,
        }),
      });
      if (!res.ok) continue;
      const body = (await res.json()) as Partial<MovieDetailsResponse>;
      const movies = (Array.isArray(body?.movies) ? body.movies : [])
        .map(asMovieDetail)
        .filter((detail): detail is MovieDetail => detail !== null);
      if (!cancelled()) onChunk(movies);
    } catch {
      // Network failure on one chunk. The next one still gets its turn.
    }
  }
}
